import { queryOptions, useQuery, type QueryClient } from '@tanstack/react-query';
import { STAFF_ROLES } from '@mybusinessmate/schema';
import { authClient } from './api';

export const sessionQueryOptions = queryOptions({
  queryKey: ['session'] as const,
  queryFn: async () => {
    const { data, error } = await authClient.getSession();
    if (error) throw error;
    // `null` when signed out - a normal state, not an error.
    return data;
  },
  staleTime: 60_000,
});

export type Session = NonNullable<Awaited<ReturnType<NonNullable<typeof sessionQueryOptions.queryFn>>>>;

export const useSession = () => useQuery(sessionQueryOptions);

export const isStaffRole = (role: string | null | undefined): boolean =>
  !!role && (STAFF_ROLES as readonly string[]).includes(role);

/**
 * Call right after anything that changes who is signed in (sign-in, verify, 2FA).
 *
 * `invalidateQueries` is not enough here: it only refetches queries something is watching, and route guards read
 * the session with `ensureQueryData`, which happily returns a stale cached "signed out". That left the guard
 * bouncing the user straight back to sign-in even though the server had just created their session.
 * This forces a real fetch, and drops the cached profile so it is never another account's.
 */
export async function refreshSession(queryClient: QueryClient) {
  queryClient.removeQueries({ queryKey: ['business', 'profile'] });
  return queryClient.fetchQuery({ ...sessionQueryOptions, staleTime: 0 });
}

/** Shown when a sign-in succeeds but the follow-up session read comes back signed out (cookie not stored or not sent). */
export const SESSION_NOT_KEPT_MESSAGE =
  'You’re signed in, but this browser didn’t keep the session. Allow cookies for this site and try again.';
